import { ArrowRight, Clock, Lock, XCircle, AlertCircle } from "lucide-react";
import { useHistory } from "react-router-dom";
import { cn } from "@/utils/classnames";
import { useApplicationInfo } from "@/queries/provider-onboarding/use-application-info";
import { APPLICATION_STATUS } from "@/queries/provider-onboarding/application-status";
import {
  PROVIDER_TYPE,
  getResumeStep,
} from "@/pages/merchant-processing/constants";
import { useClientSettings } from "@/queries/client-settings/use-client-settings";
import { useUpdateInvoiceSettings } from "@/queries/client-settings/use-update-invoice-settings";
import { SettingKey, SettingCategory } from "@/queries/client-settings/types";

interface MerchantProcessingSectionProps {
  userRole: string | null;
  userId: string;
}

const SUPPORT_EMAIL = "support@pocketbook.tech";

const LockedBanner = () => (
  <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 mb-4">
    <Lock className="h-4 w-4 text-amber-500 shrink-0" />
    <p className="text-sm text-amber-700">
      These settings are locked until merchant processing onboarding is
      complete.
    </p>
  </div>
);

const Toggle = ({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
}) => (
  <div className="flex items-center gap-3 mt-3">
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
        checked ? "bg-green-500" : "bg-gray-200",
        disabled && "cursor-not-allowed opacity-50",
      )}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
          checked ? "translate-x-6" : "translate-x-1",
        )}
      />
    </button>
    <span className="text-sm text-gray-700">{label}</span>
  </div>
);

const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <div className="px-5 py-4 border-b border-gray-200">
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
    </div>
    <div className="px-5 py-4">{children}</div>
  </div>
);

export const MerchantProcessingSection = ({
  userRole,
  userId,
}: MerchantProcessingSectionProps) => {
  const history = useHistory();
  const canManage = userRole !== "admin" && userRole !== "auth_user";

  const { data: applicationInfo, isLoading } = useApplicationInfo(
    userId,
    PROVIDER_TYPE,
  );

  const { data: settings } = useClientSettings(
    { userId, category: "integrations" },
    { enabled: !!userId },
  );

  const deluxeSetting = settings?.find(
    (s) => s.key === SettingKey.DELUXE_INVOICE_DEFAULT_CC,
  );
  const ccDefault = deluxeSetting?.value?.deluxe_invoice_default_cc ?? false;

  const surchargeSetting = settings?.find(
    (s) => s.key === SettingKey.DELUXE_HAS_CUSTOMER_SURCHARGE,
  );
  const hasSurcharge = surchargeSetting?.value?.has_customer_surcharge ?? false;

  const { mutate: updateSetting } = useUpdateInvoiceSettings();

  const handleCCDefaultToggle = (value: boolean) => {
    updateSetting({
      userId,
      payload: {
        category: SettingCategory.INTEGRATIONS,
        key: SettingKey.DELUXE_INVOICE_DEFAULT_CC,
        value: { deluxe_invoice_default_cc: value },
      },
    });
  };

  const status = applicationInfo?.status;
  const hasApplication = !!applicationInfo?.external_application_id;
  const isInProgress =
    hasApplication &&
    status !== APPLICATION_STATUS.APPLICATION_SUBMITTED &&
    status !== APPLICATION_STATUS.APPLICATION_APPROVED &&
    status !== APPLICATION_STATUS.APPLICATION_REJECTED &&
    status !== APPLICATION_STATUS.APPLICATION_NEEDS_INFO;
  const isPendingReview = status === APPLICATION_STATUS.APPLICATION_SUBMITTED;
  const isApproved = status === APPLICATION_STATUS.APPLICATION_APPROVED;
  const isRejected = status === APPLICATION_STATUS.APPLICATION_REJECTED;
  const needsInfo = status === APPLICATION_STATUS.APPLICATION_NEEDS_INFO;
  const isOnboarded = isApproved;

  return (
    <div className="space-y-4 max-w-2xl">
      {canManage && isLoading && (
        <SectionCard title="Merchant Processing">
          <div className="space-y-2 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-8 bg-gray-200 rounded w-40 mt-4" />
          </div>
        </SectionCard>
      )}

      {canManage && !isLoading && (
        <SectionCard title="Merchant Processing">
          {!hasApplication && (
            <>
              <p className="text-sm leading-relaxed text-gray-500">
                Want to receive card payments via credit or debit card? Apply
                for merchant processing in just a few easy steps.
              </p>
              <button
                type="button"
                onClick={() => history.push("/invoices/settings/merchant")}
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-gray-900 hover:bg-gray-700 px-4 py-2 text-sm font-semibold text-white transition-colors"
              >
                Apply for Merchant Processing
                <ArrowRight className="h-4 w-4" />
              </button>
            </>
          )}

          {isInProgress && (
            <>
              <p className="text-sm leading-relaxed text-gray-500">
                You have an in-progress merchant processing application. Pick up
                where you left off.
              </p>
              <button
                type="button"
                onClick={() =>
                  history.push(
                    getResumeStep(
                      status!,
                      applicationInfo!.external_application_id,
                    ),
                  )
                }
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-gray-900 hover:bg-gray-700 px-4 py-2 text-sm font-semibold text-white transition-colors"
              >
                Continue Merchant Processing
                <ArrowRight className="h-4 w-4" />
              </button>
            </>
          )}

          {isPendingReview && (
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Application under review
                </p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Your application has been submitted and is being reviewed.
                  This typically takes 1–2 business days. You'll receive an
                  email once a decision is made.
                </p>
              </div>
            </div>
          )}

          {isApproved && (
            <div className="flex items-start gap-3">
              <div className="h-5 w-5 rounded-full bg-green-500 flex items-center justify-center shrink-0 mt-0.5">
                <svg
                  className="h-3 w-3 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Merchant processing active
                </p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Your application has been approved. Card payments are now
                  available.
                </p>
              </div>
            </div>
          )}

          {isRejected && (
            <div className="flex items-start gap-3">
              <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Application not approved
                </p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Unfortunately your application was not approved. Please{" "}
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="underline text-gray-700 hover:text-gray-900"
                  >
                    contact support
                  </a>{" "}
                  for more information.
                </p>
              </div>
            </div>
          )}

          {needsInfo && (
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Additional information required
                </p>
                <p className="text-sm text-gray-500 mt-0.5">
                  We need a bit more information to process your application.
                  Please check your email or{" "}
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="underline text-gray-700 hover:text-gray-900"
                  >
                    contact support
                  </a>
                  .
                </p>
              </div>
            </div>
          )}
        </SectionCard>
      )}

      <SectionCard title="Default Card Payment Setting">
        {!isOnboarded && <LockedBanner />}
        <p className="text-sm leading-relaxed text-gray-500">
          Set the default for card payments. When <strong>ON</strong>, card
          payments will be enabled for invoices and QuickPay by default, but can
          be turned off before sending an individual invoice. When{" "}
          <strong>OFF</strong>, card payments are disabled by default but can
          still be enabled when creating an individual invoice.
        </p>
        <Toggle
          label="Yes, Accept Card Payments by Default"
          checked={ccDefault}
          disabled={!isOnboarded}
          onChange={handleCCDefaultToggle}
        />
      </SectionCard>

      <SectionCard title="Customer Surcharge">
        {!isOnboarded && <LockedBanner />}

        <p className="text-sm leading-relaxed text-gray-500">
          The default for applying merchant processing fees to your customer.
          Your customer will see a 3.0% surcharge listed before they make their
          payment. To update this setting, please{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="underline text-gray-600 hover:text-gray-900"
          >
            contact support.
          </a>
        </p>
        <Toggle
          label="Customer surcharge enabled"
          checked={hasSurcharge}
          disabled
        />
      </SectionCard>
    </div>
  );
};
